import React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Link, Typography, useTheme } from "@mui/material";
import {
  Subscription,
  AggregatedFundCommissionStatistics,
} from "@rentablo/api/apiv1";
import OneClickIcon from "@icons/myicons/oneclick.svg?react";
import { useFeatureFlag } from "@hooks";
import {
  CashAccountsCard,
  CreditCardsCard,
  InvestmentsCard,
  CashbackSuggestionCard,
  CashbackNotificationStatus,
} from "@components";
import { Banner } from "@rentablo/components";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { GoalsDashboardTile } from "@features/goals/components/dashboard/GoalsDashboardTile";

const AssetsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: ${({ theme }) => theme.spacing(4)};
  width: 100%;
`;

// The margin on the row, not the heading: centring would count the heading's
// uneven margins and set it lower than the link.
const AssetsHeaderWrapper = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(3),
  margin: theme.spacing(6, 0, 4),
}));

interface AssetsProps {
  investmentsCount: number;
  cashAccountsBalance: number;
  currency: string;
  availableCashbackSubscriptions: Subscription[];
  netfondsImportStatus: boolean | null;
  fundCommissions?: AggregatedFundCommissionStatistics;
  subscriptions?: Subscription[];
  openAddModal: () => void;
}

export const Assets = (props: AssetsProps) => {
  const {
    investmentsCount,
    cashAccountsBalance,
    currency,
    availableCashbackSubscriptions,
    netfondsImportStatus,
    fundCommissions,
    subscriptions,
    openAddModal,
  } = props;
  const { groupedAccountIds, isFetching } = useDashboardSelectedGroupsContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const anyActive = subscriptions?.some(
    (subscription) => subscription.status === "processed"
  );

  const shouldShowCashbackSuggestion =
    subscriptions &&
    !subscriptions.some((subscription) =>
      [
        "in_progress",
        "internal_processing",
        "processing_at_bank",
        "processed",
      ].includes(subscription.status || "")
    ) &&
    fundCommissions?.cashbackAdvantage?.commissionStatus === "OPTIONAL";

  const isCashbackEnabled = useFeatureFlag("CASHBACK_WIZARD");
  const isGoalsEnabled = useFeatureFlag("GOALS");
  const isCashbackStatusShown =
    Boolean(availableCashbackSubscriptions.length) && isCashbackEnabled;

  return (
    <div>
      {netfondsImportStatus !== null && anyActive && (
        <Banner
          type="info"
          icon={<OneClickIcon width="20px" height="20px" />}
          title={t("dashboardPage.oneClick.title")}
          text={t("dashboardPage.oneClick.text")}
          primaryButton={{
            label: t("dashboardPage.oneClick.buttonText"),
            onClick: () => navigate("/cashback-import"),
          }}
          sx={{ marginTop: theme.spacing(6) }}
        />
      )}
      <AssetsHeaderWrapper>
        <Typography variant="body3" color="textSecondary" fontWeight={600}>
          {t("dashboardPage.allYourAssets")}
        </Typography>
        <Link onClick={() => navigate("/investments")} underline="hover">
          <Typography variant="body3">
            {t("dashboardPage.investmentsLink")}
          </Typography>
        </Link>
      </AssetsHeaderWrapper>

      <AssetsWrapper>
        <InvestmentsCard
          investmentsCount={investmentsCount}
          currency={currency}
          onAddAccountsClick={() => {
            navigate("/import-bank-connection/select-bank");
          }}
          onClick={() => {
            navigate("/investments");
          }}
        />
        <CashAccountsCard
          areAccountsLoading={isFetching}
          cashAccountsIds={groupedAccountIds.cashIds}
          balance={cashAccountsBalance}
          currency={currency}
          onAddAccountsClick={() => {
            navigate("/import-bank-connection/select-bank");
          }}
          onClick={() => {
            navigate("/accounts");
          }}
        />
        <CreditCardsCard
          currency={currency}
          onAddAccountsClick={() => openAddModal()}
          onClick={() => navigate("/depots-accounts")}
        />
        {isGoalsEnabled && <GoalsDashboardTile />}
        {isCashbackStatusShown && (
          <CashbackNotificationStatus
            subscriptions={availableCashbackSubscriptions}
          />
        )}
        {shouldShowCashbackSuggestion && (
          <CashbackSuggestionCard
            fundCommissions={fundCommissions}
            currency={currency}
          />
        )}
      </AssetsWrapper>
    </div>
  );
};
